/**
 * AI screen generation — "create/update from design". Reads the design
 * sections, asks the model (SCREEN_WIZARD) to lay out every screen the game
 * needs as wireframe zone blockouts, sanitises the result, and upserts each
 * screen (matched by name so re-running maintains existing screens rather than
 * duplicating them). Ported from Anvil's ScreenWizard.
 */
import type { DesignProject, Screen } from '@volley/crucible-ipc-types';
import type { CrucibleClient } from '@volley/crucible-client';
import { aiChat, readLens, readSection } from './anvil-api';
import { SCREEN_WIZARD_GUIDE, SCREEN_WIZARD_TASK } from './prompts';
import { ProposedScreensSchema, parseAiJson } from './schemas';
import { sanitizeZone } from './lib/zone-kinds';

let idCounter = 0;
function freshId(prefix: string): string {
  idCounter += 1;
  return `${prefix}-${Date.now().toString(36)}-${idCounter}`;
}

function screenIdentityKey(name: string, surface: Screen['surface']): string {
  return `${name.trim().toLowerCase()}|${surface}`;
}

export interface GenerateScreensArgs {
  gameId: string;
  project: DesignProject;
  existing: Screen[];
  lensIds?: { id: string; kind: 'agent' | 'skill' }[];
  /** Persist a screen (the useScreens hook's `save`); keeps the list in sync. */
  save: (screen: Screen) => Promise<Screen>;
}

export async function generateScreensFromDesign(
  client: CrucibleClient,
  args: GenerateScreensArgs,
): Promise<Screen[]> {
  const { gameId, project, existing, lensIds = [], save } = args;

  // Lens block (optional).
  let lensBlock = '';
  if (lensIds.length > 0) {
    const parts: string[] = ['\n\n# Active lenses\n'];
    for (const { id, kind } of lensIds) {
      try {
        const full = await readLens(client, id, kind);
        parts.push(`\n## Lens: ${full.name}\n${full.description}\n\n${full.body}\n`);
      } catch {
        /* skip */
      }
    }
    lensBlock = parts.join('');
  }

  // Design context — every section body.
  const ordered = [...project.sections].sort((a, b) => a.order - b.order);
  const dumpParts: string[] = [];
  for (const s of ordered) {
    let body = '';
    try {
      body = await readSection(client, gameId, s.filename);
    } catch {
      /* skip unreadable */
    }
    dumpParts.push(`\n## ${s.title}\n${body}`);
  }

  const system = SCREEN_WIZARD_GUIDE + lensBlock;
  const user = `Project: ${project.name} (${project.projectType.toUpperCase()})\n\nDesign:\n${dumpParts.join('\n')}\n\n${SCREEN_WIZARD_TASK}`;

  const resp = await aiChat(client, {
    gameId,
    system,
    messages: [{ role: 'user', content: user }],
    maxTokens: 16384,
  });
  const parsed = parseAiJson(resp.text, ProposedScreensSchema);

  // Upsert by name + surface so companion layouts can share user-facing names.
  const byName = new Map(existing.map((s) => [screenIdentityKey(s.name, s.surface), s]));
  const seenProposalNames = new Set<string>();
  const written: Screen[] = [];
  for (const proposed of parsed.screens ?? []) {
    const surface = proposed.surface;
    const normalizedName = screenIdentityKey(proposed.name, surface);
    if (seenProposalNames.has(normalizedName)) {
      continue;
    }
    seenProposalNames.add(normalizedName);
    const match = byName.get(normalizedName);
    const screen: Screen = {
      id: match?.id ?? freshId('screen'),
      name: proposed.name,
      surface,
      description: proposed.description ?? '',
      zones: (proposed.zones ?? []).map((z, i) =>
        sanitizeZone({ ...z, zIndex: i }, surface, freshId('zone')),
      ),
      linkedSectionIds: match?.linkedSectionIds ?? [],
      createdAt: match?.createdAt ?? '',
      updatedAt: '',
    };
    const saved = await save(screen);
    byName.set(normalizedName, saved);
    written.push(saved);
  }
  return written;
}
